import { nanoid } from "nanoid";
import debug from "debug";

const log = debug("app:txt");

/**
 * @category Components
 * @subcategory UndoRedoManager
 */

export default class EditReplaceAction {
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.setEventListeners();
  }

  setEventListeners() {
    log("event lsisteners started");
    this.eventHub.on("editChanged", (data) => this.processEdit(data));
  }

  cleanup() {
    if (!this.eventHub) return; // Prevent errors if already cleaned up

    log("Cleaning up EditReplaceAction...");

    // Unregister event listener for editChanged event
    this.eventHub.off("editChanged", this.processEdit);

    // Clear references
    this.experience = null;
    this.eventHub = null;

    log("EditReplaceAction cleanup completed.");
  }

  /**
   * Initializes the appropriate action based on event type.
   * @param {Object} payload - Event payload with type and data.
   */
  handleAction(payload) {
    const { eventType, data } = payload;

    switch (eventType) {
      case "replace":
        this.processReplace(data);
        break;
      case "edit":
        this.processEdit(data);
        break;
      default:
        log("Unhandled action type:", eventType);
        break;
    }
  }

  /**
   * Processes a "replace" action.
   * @param {Object} data - The data related to the replace action.
   */
  processReplace(data) {
    const { oldPart, newPart } = data;

    if (!oldPart || !newPart) {
      throw new Error("Missing oldPart or newPart in replace action data.");
    }

    // Ensure oldPart has a valid objectId
    if (!oldPart.partData?.objectId) {
      throw new Error("Invalid or missing objectId in oldPart.");
    }

    // Assign a new objectId to newPart
    newPart.partData = { ...newPart.partData, objectId: nanoid() };
    log("Assigned new objectId to newPart:", newPart.partData.objectId);

    // Emit delete and make events for the replacement
    this.eventHub.emit("deleteObjects", { figures: [oldPart] });
    this.eventHub.emit("makeParts", { figures: [newPart] });
  }

  /**
   * Processes an "edit" action.
   * @param {Object} data - The data related to the edit action.
   */
  processEdit(data) {
    log("data", data);
    const { objectId, mode, previousState, changedState } = data;

    if (!objectId || !changedState || !mode) {
      log("Invalid data for edit action:", data);
      return;
    }

    // Determine mode and delegate processing accordingly
    if (mode === "textbox") {
      this.processTextbox(objectId, changedState);
    } else {
      this.processPart(data);
    }
  }

  processPart(data) {
    const { objectId, mode, previousState, changedState } = data;
    log("previousState, new props, mode", previousState, changedState, mode);
    const objectIdMap = this.experience.undoRedoVault.objectIdMap;
    const uuid = objectIdMap.get(objectId);
    const targetObject = uuid
      ? this.experience.scene.getObjectByProperty("uuid", uuid)
      : null;

    if (!targetObject) {
      log("No object found with provided objectId:", objectId);
      return;
    }
    this.updatePartProps(targetObject, changedState, mode);
    this.eventHub.emit("textureChange", targetObject);

    log("Updated part properties and mode:", targetObject.userData);

    // Emit an event for UI or further updates
    this.eventHub.emit("textureChange", targetObject);
    this.eventHub.emit("xmd");
  }

  updatePartProps(targetObject, changedState, mode) {
    targetObject.userData = {
      ...targetObject.userData,
      ...changedState,
    };
    targetObject.userData.mode = mode;
  }

  processTextbox(objectId, changedState) {
    log("Textbox edit detected; logging for now:", { objectId, changedState });
    // Additional logic for textboxes will go here in future
  }
}
